import { useState } from "react";

// Cursor-based pagination state for the v2 deliveries endpoints.
// The backend caps offset paging at page * limit <= 10000 (Elasticsearch's
// result window), so deep navigation must follow `nextCursor` instead.
//
// - `cursor` is sent as `?cursor=<value>` (undefined on the first page).
// - `history` is the stack of previous cursors, enabling a Prev button.
// - When `resetKey` changes (i.e. the active filter set changed), paging resets
//   to the first page. We do this during render (React's documented "adjust state
//   during render" pattern) rather than in an effect, so the reset lands in the
//   same commit as the new filter — avoiding a stale fetch with the old cursor.
//   The tracker is state, not a ref: mutating a ref here would be swallowed under
//   StrictMode's double render (the second pass sees the mutated ref and skips).
const useCursorPagination = (resetKey) => {
  const [cursor, setCursor] = useState(undefined);
  const [history, setHistory] = useState([]);
  const [trackedKey, setTrackedKey] = useState(resetKey);

  if (trackedKey !== resetKey) {
    setTrackedKey(resetKey);
    setCursor(undefined);
    setHistory([]);
  }

  const goNext = (nextCursor) => {
    if (!nextCursor) return;
    setHistory((prev) => [...prev, cursor]);
    setCursor(nextCursor);
  };

  const goPrev = () => {
    setHistory((prev) => {
      if (!prev.length) return prev;
      const next = [...prev];
      const previousCursor = next.pop();
      setCursor(previousCursor);
      return next;
    });
  };

  return {
    cursor,
    pageIndex: history.length + 1,
    canPrev: history.length > 0,
    goNext,
    goPrev,
  };
};

export default useCursorPagination;
